import type { ChatUiMoney } from '../../shared/schema/money_schema.js';

/** Uma janela de cancelamento do fornecedor, já normalizada.
 *
 *  SEMÂNTICA (a parte que engana): a janela diz **"cancelar entre `from` e
 *  `until` custa `penalty`"** — `penalty` é MULTA, não reembolso. A prova está
 *  no dado: o fornecedor manda `isRefundable: true` junto de uma janela com
 *  valor ZERO (`BookingConditions`), e "reembolsável com reembolso zero" seria
 *  contradição; e no detalhe a multa é R$ 758,50 sobre um total de R$ 761,50 —
 *  o clássico "cancelou dentro da janela, perde tudo", com o `until` caindo no
 *  próprio check-in. */
export type HotelCancellationWindow = {
  readonly from?: string;
  readonly until?: string;
  readonly penalty?: ChatUiMoney;
};

/** A data-calendário COMO O FORNECEDOR ESCREVEU, por fatia de string.
 *
 *  Converter por UTC (`new Date(...).toISOString()`) empurra o dia inteiro
 *  quando o horário tem fuso: `2026-09-01T23:30:00-03:00` vira `2026-09-02` e a
 *  tela passa a prometer um dia a mais de cancelamento grátis. O prazo é o do
 *  balcão do hotel, não o de Greenwich.
 *
 *  O ida-e-volta no fim NÃO é redundante com o `Date.parse`: `2026-02-30` parseia
 *  numa boa (o motor rola para 02/03), passaria a fatia e chegaria ao
 *  `z.iso.date()` do wire, que rejeita data de calendário inválida — derrubando
 *  o parse do QUARTO INTEIRO e, com ele, a resposta de detalhes. Mesma cerca que
 *  `hotel_rooms_builder` já monta pela mesma razão. */
function isoDate(value: unknown): string | undefined {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value)) return undefined;
  const day = value.slice(0, 10);
  const parsed = Date.parse(`${day}T00:00:00Z`);
  return Number.isFinite(parsed) && new Date(parsed).toISOString().slice(0, 10) === day
    ? day
    : undefined;
}

/** A multa em qualquer uma das duas formas: `value: { value, currency }` como o
 *  fornecedor manda, ou `penalty: { amount, currency }` como já a projetamos. */
function penalty(policy: Record<string, unknown>): ChatUiMoney | undefined {
  const source = policy.value ?? policy.penalty;
  if (!source || typeof source !== 'object') return undefined;
  const raw = source as Record<string, unknown>;
  const amount = raw.value ?? raw.amount;
  const currency = raw.currency;
  /* Multa NEGATIVA não existe (seria o hotel pagando para você desistir): é
   * payload torto, e deixá-la entrar faria a faixa lê-la como cortesia. Fica
   * fora já aqui, para o valor nem chegar ao wire. */
  return typeof amount === 'number' &&
    Number.isFinite(amount) &&
    amount >= 0 &&
    typeof currency === 'string' &&
    currency.trim()
    ? { amount, currency: currency.trim() }
    : undefined;
}

/** Normaliza `cancellationPolicies` — e aceita as DUAS formas do mesmo quarto,
 *  pela mesma razão que `HotelRoomsSourceRoom` aceita: o cru do fornecedor
 *  (`{ startDate, endDate, value }`, passthrough sem forma) e o já projetado
 *  (`{ from, until, penalty }`), que hoje também viaja no wire. Ler só o cru
 *  faria a janela pronta voltar vazia, e o vazio some calado — o tipo de erro
 *  que não aparece em teste nenhum. Janela sem data nem multa não descreve nada
 *  e cai fora. */
export function hotelCancellationWindows(
  policies: readonly unknown[] | undefined,
): HotelCancellationWindow[] {
  if (!policies?.length) return [];
  return policies
    .map((policy) => {
      if (!policy || typeof policy !== 'object') return undefined;
      const raw = policy as Record<string, unknown>;
      const from = isoDate(raw.startDate ?? raw.from);
      const until = isoDate(raw.endDate ?? raw.until);
      const charge = penalty(raw);
      const window = {
        ...(from ? { from } : {}),
        ...(until ? { until } : {}),
        ...(charge ? { penalty: charge } : {}),
      };
      return (from ?? until ?? charge) ? window : undefined;
    })
    .filter((window): window is HotelCancellationWindow => window !== undefined)
    .slice(0, 6);
}

/** Até quando dá para cancelar de graça, como data ISO (YYYY-MM-DD).
 *
 *  A conta ÓBVIA está errada e já custou caro: pegar o `until` mais cedo devolve
 *  o FIM da janela de multa (≈ o check-in), então a tela anunciava cancelamento
 *  grátis para DEPOIS de a cobrança já ter começado. O grátis acaba quando a
 *  primeira multa COMEÇA.
 *
 *  Duas formas aparecem no dado e a regra cobre as duas: quando há janela de
 *  multa, o limite é o `from` dela; quando o fornecedor manda só uma janela
 *  explícita de multa ZERO, o limite é o `until` dessa janela. Fora isso não
 *  afirmamos nada — sub-afirmar é seguro, super-afirmar mente sobre dinheiro,
 *  e é por isso que tarifa marcada não-reembolsável nunca anuncia grátis.
 *
 *  Janela SEM multa declarada não conta como grátis: o fornecedor marca a
 *  cortesia explicitamente (`value: 0`), então ausência é dado faltando, não
 *  isenção — tratá-la como grátis inventaria prazo em cima de payload torto. */
export function hotelFreeCancellationUntil(
  windows: readonly HotelCancellationWindow[],
  refundable?: boolean,
): string | undefined {
  if (refundable === false) return undefined;
  const charged = windows.filter((window) => (window.penalty?.amount ?? 0) > 0);
  if (charged.length > 0) {
    const starts = charged.map((window) => window.from).filter((from) => from !== undefined);
    return starts.reduce<string | undefined>(
      (earliest, from) => (earliest === undefined || from < earliest ? from : earliest),
      undefined,
    );
  }
  const free = windows
    .filter((window) => window.penalty?.amount === 0)
    .map((window) => window.until)
    .filter((until) => until !== undefined);
  return free.reduce<string | undefined>(
    (latest, until) => (latest === undefined || until > latest ? until : latest),
    undefined,
  );
}
